/**
 * Who may inspect the chat's tool-execution internals (UX-6).
 *
 * Every tool row keeps its "Used tool: <name>" line for everyone — a user is entitled to
 * see THAT the assistant did something on their behalf, and the line is what makes a turn
 * auditable. What is gated is the row's expandable body: the raw arguments and the raw
 * result. Those are orchestration internals (`response_grep`, `instrument-search_standard`,
 * internal ids, prompt-shaped payloads) that mean nothing to an investment professional and
 * expose how the agent is wired.
 *
 * Privileged sessions — ROOT, and any impersonated session — keep the full disclosure,
 * because that body is the only debugging surface there is when a user reports "the bot did
 * the wrong thing": support reproduces the conversation while impersonating them and reads
 * the arguments the model actually sent. ADMIN is deliberately NOT privileged here: it is a
 * customer-side role (it administers users, cf. AdvanceSettings), so an admin is still an end
 * user as far as the orchestration is concerned.
 *
 * Impersonation is read from `impersonateEmail$` rather than `user$.impersonating`: while
 * impersonating, `user$` holds the IMPERSONATEE (their roles, so the role check alone would
 * fail), and the `user-update` SSE listener re-runs `updateUser(userInfo)` without the
 * impersonating flag, which flips that field back to false mid-session. The atom is the
 * ground truth — it is what the request interceptor reads to send `X-Impersonate-Email`.
 */
import { useUserValue } from "$root/functional-areas/user";
import { impersonateEmail$ } from "$root/functional-areas/session/atom";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";

/**
 * The one role that keeps the expandable tool body. Compared normalized (see
 * {@link normalizeRole}) because the backend's role strings are not a guaranteed shape: they
 * arrive uppercase and have been seen bracketed (`"[ROOT]"`) — cf. the same defensive strip in
 * AdvanceSettings' UserSettingsPanel — while the `UserRole` map in functional-areas/user also
 * carries lowercase variants. Getting this wrong fails SILENTLY (root sees a regular user's
 * view and support loses its only debugging surface), so it is worth the three lines.
 */
const PRIVILEGED_ROLE = "ROOT";

/** A backend role string reduced to a bare uppercase token (`"[root]"` → `"ROOT"`). */
function normalizeRole(role: string): string {
	return role.replace(/[^A-Za-z]/g, "").toUpperCase();
}

/**
 * Whether this session may see tool arguments and results. Pure, so the policy is testable
 * without a store: {@link useToolDetailsVisible} is the wiring.
 */
export function canSeeToolDetails({
	roles,
	impersonating,
}: {
	roles: readonly string[];
	impersonating: boolean;
}): boolean {
	return impersonating || roles.some((role) => normalizeRole(role) === PRIVILEGED_ROLE);
}

/** {@link canSeeToolDetails} for the current session. */
export function useToolDetailsVisible(): boolean {
	const { roles } = useUserValue();
	const impersonateEmail = useAtomValue(impersonateEmail$);
	return canSeeToolDetails({ roles, impersonating: Boolean(impersonateEmail) });
}
